/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Typography2 } from '@coursera/cds-core';

import type { AIMessageDraft } from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/types';
import { mockCompileEmailHtml } from 'bundles/enterprise-admin-messaging-v2/components/common/mockCompileEmailHtml';

import _t from 'i18n!nls/enterprise-admin-messaging-v2';

type Props = {
  draft: AIMessageDraft;
  /** Sender display name shown next to the From label. */
  fromName: string;
  /** Sender email address shown next to the From label. */
  fromEmail: string;
  /** Recipient address shown next to the To label. */
  toAddress: string;
  /** Namespaces the subject / iframe test ids so each screen stays addressable. */
  dataTestIdPrefix: string;
};

const styles = {
  body: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-200);
  `,
  metadata: css`
    display: grid;
    grid-template-columns: max-content 1fr;
    column-gap: var(--cds-spacing-150);
    row-gap: var(--cds-spacing-50);
  `,
  metadataLabel: css`
    color: var(--cds-color-neutral-primary);
  `,
  metadataValue: css`
    color: var(--cds-color-neutral-primary-weak);
    word-break: break-word;
  `,
  /* The iframe sits inside this card; the card supplies the rounded outer
     frame and the iframe paints the email body. Height matches the design
     mock — the iframe scrolls internally if the body overflows. */
  emailFrame: css`
    border: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    border-radius: var(--cds-border-radius-100);
    overflow: hidden;
    background: var(--cds-color-neutral-background-primary-weak);
  `,
  iframe: css`
    display: block;
    width: 100%;
    height: 480px;
    border: 0;
  `,
};

/**
 * Email From / To / Subject metadata plus the compiled email rendered inside a
 * sandboxed iframe so the email's inline styles can't leak into the app (and
 * vice versa). Shared by the Preview-and-send and Message-details screens; the
 * caller owns the surrounding `Dialog.Content` and any action bar.
 */
const EmailPreviewBody = ({ draft, fromName, fromEmail, toAddress, dataTestIdPrefix }: Props) => {
  const compiledHtml = React.useMemo(() => mockCompileEmailHtml(draft), [draft]);

  return (
    <div css={styles.body}>
      <div css={styles.metadata}>
        <Typography2 component="span" variant="subtitleSmall" css={styles.metadataLabel}>
          {_t('From')}
        </Typography2>
        <Typography2 component="span" variant="bodySecondary" css={styles.metadataValue}>
          {`${fromName} <${fromEmail}>`}
        </Typography2>
        <Typography2 component="span" variant="subtitleSmall" css={styles.metadataLabel}>
          {_t('To')}
        </Typography2>
        <Typography2 component="span" variant="bodySecondary" css={styles.metadataValue}>
          {toAddress}
        </Typography2>
        <Typography2 component="span" variant="subtitleSmall" css={styles.metadataLabel}>
          {_t('Subject')}
        </Typography2>
        <Typography2
          component="span"
          variant="bodySecondary"
          css={styles.metadataValue}
          data-testid={`${dataTestIdPrefix}-subject`}
        >
          {draft.subject}
        </Typography2>
      </div>
      <div css={styles.emailFrame}>
        <iframe
          title={_t('Email preview')}
          srcDoc={compiledHtml}
          sandbox=""
          referrerPolicy="no-referrer"
          css={styles.iframe}
          data-testid={`${dataTestIdPrefix}-iframe`}
        />
      </div>
    </div>
  );
};

export default EmailPreviewBody;
